import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, IconButton, Button, Divider } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
    ArrowBackOutlined, FlashOnOutlined, CheckCircleOutline,
    LockOutlined, NorthEastOutlined,
    TvOutlined, WifiOutlined, WaterOutlined,
} from '@mui/icons-material';

const B = {
    deep:    '#0D0820',
    dark:    '#1A0F3D',
    mid:     '#2D1B69',
    purple:  '#7B5EA7',
    light:   '#9B7EC8',
    lavender:'#F7F4FC',
    ink:     '#0D0A1A',
    green:   '#00C896',
    greenDim:'#00C89618',
    red:     '#FF4D6A',
    redDim:  '#FF4D6A18',
    orange:  '#FF6B35',
};

const BILL_CATEGORIES = [
    { value: 'electricity', label: 'Electricity', icon: <FlashOnOutlined />, color: '#F5C842', emoji: '⚡' },
    { value: 'tv',          label: 'TV / Cable',  icon: <TvOutlined />,      color: '#007AFF', emoji: '📺' },
    { value: 'internet',    label: 'Internet',    icon: <WifiOutlined />,    color: '#34C759', emoji: '📶' },
    { value: 'water',       label: 'Water',       icon: <WaterOutlined />,   color: '#5AC8FA', emoji: '💧' },
];

const BILLERS: Record<string, { value: string; label: string }[]> = {
    electricity: [
        { value: 'ekedc',  label: 'EKEDC (Ikeja Electric)' },
        { value: 'aedc',   label: 'AEDC (Abuja Electric)' },
        { value: 'phcn',   label: 'PHCN' },
        { value: 'ibedc',  label: 'IBEDC' },
    ],
    tv: [
        { value: 'dstv',      label: 'DSTV' },
        { value: 'gotv',      label: 'GOTV' },
        { value: 'startimes', label: 'StarTimes' },
    ],
    internet: [
        { value: 'mtn-fiber',   label: 'MTN Fiber' },
        { value: 'spectranet',  label: 'Spectranet' },
        { value: 'smile',       label: 'Smile Network' },
    ],
    water: [
        { value: 'lagos-water', label: 'Lagos Water Corp' },
        { value: 'abuja-water', label: 'Abuja Water Board' },
    ],
};

const QUICK_AMOUNTS = [1000, 2000, 5000, 10000, 20000, 50000];

const RECENT_TXS = [
    { id: 1, title: 'Electricity Bill',  sub: 'EKEDC · 01234567890', amount: 15000, date: 'Yesterday · 6:45 PM' },
    { id: 2, title: 'DSTV Subscription', sub: 'Smart Card · 789 ***', amount: 8000,  date: 'Last week · 2:10 PM' },
    { id: 3, title: 'Internet Bill',      sub: 'Spectranet · Renewal', amount: 12000, date: '14 Mar · 9:00 AM'   },
    { id: 4, title: 'Electricity Bill',   sub: 'EKEDC · 01234567890', amount: 15000, date: '1 Mar · 8:30 AM'    },
];

const fmt = (n: number) =>
    new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(n);

const BillsPage: React.FC = () => {
    const navigate = useNavigate();
    const theme    = useTheme();
    const isDark   = theme.palette.mode === 'dark';

    const [step,       setStep]       = useState<1 | 2>(1);
    const [category,   setCategory]   = useState('');
    const [biller,     setBiller]     = useState('');
    const [accountNo,  setAccountNo]  = useState('');
    const [amount,     setAmount]     = useState('');
    const [loading,    setLoading]    = useState(false);
    const [done,       setDone]       = useState(false);

    const balance   = 125000;
    const numAmount = parseFloat(amount) || 0;
    const selectedCat    = BILL_CATEGORIES.find(c => c.value === category);
    const selectedBiller = (BILLERS[category] || []).find(b => b.value === biller);
    const accentColor    = selectedCat?.color ?? B.orange;

    const pageBg     = isDark ? B.deep : B.lavender;
    const surfaceBg  = isDark ? alpha(B.mid, .55) : '#fff';
    const surfaceBdr = `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`;
    const surfaceShd = `0 4px 28px ${alpha(B.dark, isDark ? .25 : .06)}`;
    const textPri    = isDark ? '#fff' : B.ink;
    const textMid    = alpha(isDark ? '#fff' : B.ink, .45);
    const textFaint  = alpha(isDark ? '#fff' : B.ink, .28);
    const dividerC   = alpha(isDark ? '#fff' : B.dark, .08);

    const glassCard = {
        background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd,
        borderRadius: '20px', backdropFilter: isDark ? 'blur(20px)' : 'none',
    };

    const handleConfirm = () => {
        setLoading(true);
        setTimeout(() => { setLoading(false); setDone(true); }, 1800);
    };

    const goBack = () => {
        if (step === 1) navigate(-1);
        else setStep(1);
    };

    const canProceed = category && biller && accountNo && numAmount > 0;

    /* ── SUCCESS ── */
    if (done) return (
        <Box sx={{ minHeight: '100vh', background: isDark ? `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)` : B.lavender, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 3 }}>
            <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ width: 80, height: 80, borderRadius: '50%', background: B.greenDim, border: `2px solid ${B.green}`, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
                    <CheckCircleOutline sx={{ color: B.green, fontSize: 40 }} />
                </Box>
                <Typography sx={{ color: textPri, fontFamily: "'Instrument Serif', serif", fontSize: '1.9rem', mb: 1 }}>Bill Paid!</Typography>
                <Typography sx={{ color: textMid, fontSize: '.85rem', mb: .5 }}>{fmt(numAmount)} paid to</Typography>
                <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '1rem', mb: .25 }}>{selectedBiller?.label}</Typography>
                <Typography sx={{ color: textFaint, fontSize: '.78rem', mb: 1 }}>Account: {accountNo}</Typography>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: .75, background: alpha(B.green, .1), border: `1px solid ${alpha(B.green, .2)}`, borderRadius: 20, px: 2, py: .6, mb: 4 }}>
                    <Typography sx={{ color: B.green, fontSize: '.72rem', fontWeight: 700 }}>New Balance: {fmt(balance - numAmount)}</Typography>
                </Box>
                <Box display="flex" gap={2} justifyContent="center">
                    <Button onClick={() => { setDone(false); setStep(1); setAmount(''); setAccountNo(''); setBiller(''); setCategory(''); }}
                            sx={{ border: `1px solid ${alpha(isDark ? '#fff' : B.dark, .12)}`, color: textMid, borderRadius: 50, px: 3.5, py: 1.2, textTransform: 'none', fontWeight: 600, fontSize: '.85rem' }}>
                        Pay Another
                    </Button>
                    <Button onClick={() => navigate('/wallet')} variant="contained"
                            sx={{ background: `linear-gradient(135deg, ${B.purple}, ${B.light})`, borderRadius: 50, px: 3.5, py: 1.2, textTransform: 'none', fontWeight: 700, fontSize: '.85rem', boxShadow: `0 12px 32px ${alpha(B.purple, .4)}` }}>
                        Done
                    </Button>
                </Box>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ minHeight: '100vh', background: pageBg, pb: 10, fontFamily: "'DM Sans', system-ui, sans-serif" }}>

            {/* ══ HERO ══ */}
            <Box sx={{
                background: `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)`,
                mt: { xs: '-76px', sm: 0 },
                pt: { xs: '80px', sm: '28px' },
                pb: { xs: 4, sm: 14 }, px: 3, position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -70, right: -50, width: 240, height: 240, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.light, .18)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: -10, left: -40, width: 200, height: 200, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.orange, .08)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                <Box display="flex" alignItems="center" gap={1.5}>
                    <IconButton onClick={goBack}
                                sx={{ color: alpha('#fff', .7), background: alpha('#fff', .1), border: `1px solid ${alpha('#fff', .15)}`, width: 36, height: 36, '&:hover': { background: alpha('#fff', .2) } }}>
                        <ArrowBackOutlined sx={{ fontSize: 17 }} />
                    </IconButton>
                    <Box>
                        <Typography sx={{ color: alpha('#fff', .35), fontSize: '.62rem', letterSpacing: '.18em', textTransform: 'uppercase' }}>
                            Step {step} of 2
                        </Typography>
                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem', letterSpacing: '-.025em', fontFamily: "'Instrument Serif', serif" }}>
                            Pay Bills
                        </Typography>
                    </Box>
                    <Box sx={{ ml: 'auto', background: alpha(B.green, .15), border: `1px solid ${alpha(B.green, .3)}`, borderRadius: 20, px: 1.5, py: .5 }}>
                        <Typography sx={{ color: B.green, fontSize: '.68rem', fontWeight: 700 }}>Bal: {fmt(balance)}</Typography>
                    </Box>
                </Box>
            </Box>

            <Container maxWidth="sm" sx={{ mt: { xs: 2, sm: -8 }, position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>

                {/* ── STEP 1 ── */}
                {step === 1 && (
                    <>
                        {/* Bill Category */}
                        <Box sx={{ ...glassCard, p: 2.5, mb: 2 }}>
                            <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', mb: 2, fontWeight: 600 }}>Bill Category</Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
                                {BILL_CATEGORIES.map(cat => (
                                    <Box key={cat.value} onClick={() => { setCategory(cat.value); setBiller(''); }} sx={{
                                        borderRadius: '14px', p: 1.5, cursor: 'pointer', textAlign: 'center',
                                        background: category === cat.value ? alpha(cat.color, .12) : alpha(isDark ? '#fff' : B.dark, .03),
                                        border: `1.5px solid ${category === cat.value ? alpha(cat.color, .4) : dividerC}`,
                                        transition: 'all .18s',
                                    }}>
                                        <Typography sx={{ fontSize: '1.4rem', mb: .5 }}>{cat.emoji}</Typography>
                                        <Typography sx={{ color: category === cat.value ? cat.color : textMid, fontSize: '.65rem', fontWeight: 700, lineHeight: 1.2 }}>{cat.label}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        {/* Biller */}
                        {category && (
                            <Box sx={{ ...glassCard, p: 2.5, mb: 2 }}>
                                <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', mb: 2, fontWeight: 600 }}>Select Biller</Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {(BILLERS[category] || []).map(b => (
                                        <Box key={b.value} onClick={() => setBiller(b.value)} sx={{
                                            p: 1.5, borderRadius: '14px', cursor: 'pointer',
                                            background: biller === b.value ? alpha(accentColor, .1) : 'transparent',
                                            border: `1.5px solid ${biller === b.value ? alpha(accentColor, .35) : dividerC}`,
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            transition: 'all .18s',
                                        }}>
                                            <Typography sx={{ color: biller === b.value ? accentColor : textPri, fontWeight: 600, fontSize: '.85rem' }}>{b.label}</Typography>
                                            {biller === b.value && <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: accentColor, boxShadow: `0 0 8px ${accentColor}` }} />}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {/* Account number */}
                        {biller && (
                            <Box sx={{ ...glassCard, p: 2.5, mb: 2 }}>
                                <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', mb: 1.5, fontWeight: 600 }}>
                                    {category === 'electricity' ? 'Meter Number' : category === 'tv' ? 'Smart Card Number' : 'Account Number'}
                                </Typography>
                                <input
                                    type="tel" placeholder="Enter number"
                                    value={accountNo}
                                    onChange={e => setAccountNo(e.target.value)}
                                    style={{
                                        width: '100%', background: 'transparent', border: 'none', outline: 'none',
                                        color: textPri, fontSize: '1.1rem', fontWeight: 600,
                                        fontFamily: "'DM Sans', system-ui, sans-serif", letterSpacing: '.04em',
                                    }}
                                />
                                <Box sx={{ mt: 1.5, height: '1px', background: `linear-gradient(90deg, ${accentColor}, transparent)` }} />
                            </Box>
                        )}

                        {/* Amount */}
                        {accountNo && (
                            <Box sx={{ ...glassCard, p: 2.5, mb: 2 }}>
                                <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', mb: 1.5, fontWeight: 600 }}>Amount</Typography>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Typography sx={{ color: textMid, fontSize: '1.5rem', fontWeight: 300, fontFamily: "'Instrument Serif', serif" }}>₦</Typography>
                                    <input
                                        type="number" placeholder="0"
                                        value={amount}
                                        onChange={e => setAmount(e.target.value)}
                                        style={{
                                            flex: 1, background: 'transparent', border: 'none', outline: 'none',
                                            color: textPri, fontSize: '2rem', fontWeight: 700,
                                            fontFamily: "'Instrument Serif', serif",
                                        }}
                                    />
                                </Box>
                                <Box sx={{ mt: 1, height: '1px', background: `linear-gradient(90deg, ${accentColor}, transparent)` }} />
                                <Typography sx={{ color: textFaint, fontSize: '.7rem', mt: 1 }}>Available: {fmt(balance)}</Typography>

                                <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', mt: 2.5, mb: 1.5, fontWeight: 600 }}>Quick Select</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {QUICK_AMOUNTS.map(q => (
                                        <Box key={q} onClick={() => setAmount(String(q))} sx={{
                                            px: 1.5, py: .6, borderRadius: '20px', cursor: 'pointer', fontSize: '.75rem', fontWeight: 600,
                                            background: amount === String(q) ? alpha(accentColor, .15) : alpha(isDark ? '#fff' : B.dark, .05),
                                            color: amount === String(q) ? accentColor : textMid,
                                            border: `1px solid ${amount === String(q) ? alpha(accentColor, .35) : dividerC}`,
                                            transition: 'all .15s',
                                        }}>
                                            ₦{q.toLocaleString()}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        <Button fullWidth disabled={!canProceed} onClick={() => setStep(2)}
                                sx={{
                                    py: 1.65, borderRadius: '14px', textTransform: 'none', fontWeight: 700, fontSize: '.95rem',
                                    background: `linear-gradient(135deg, ${B.purple}, ${B.light})`,
                                    color: '#fff', boxShadow: `0 12px 32px ${alpha(B.purple, .35)}`,
                                    '&:disabled': { opacity: .35, boxShadow: 'none' },
                                    '&:hover': { boxShadow: `0 16px 40px ${alpha(B.purple, .5)}` },
                                }}>
                            Review Payment
                        </Button>
                    </>
                )}

                {/* ── STEP 2: Confirm ── */}
                {step === 2 && (
                    <>
                        <Box sx={{ ...glassCard, p: 2.5, mb: 2 }}>
                            {/* Category header */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                                <Box sx={{ width: 44, height: 44, borderRadius: '14px', background: alpha(accentColor, .12), border: `1px solid ${alpha(accentColor, .25)}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Typography sx={{ fontSize: '1.4rem' }}>{selectedCat?.emoji}</Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.9rem' }}>{selectedBiller?.label}</Typography>
                                    <Typography sx={{ color: textFaint, fontSize: '.72rem' }}>{selectedCat?.label}</Typography>
                                </Box>
                            </Box>

                            <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', mb: 2, fontWeight: 600 }}>Payment Details</Typography>

                            {[
                                { label: 'Account / Meter', value: accountNo },
                                { label: 'Amount',           value: fmt(numAmount) },
                                { label: 'Fee',              value: 'Free' },
                                { label: 'Total Debit',      value: fmt(numAmount) },
                            ].map((row, i, arr) => (
                                <React.Fragment key={row.label}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" py={1.25}>
                                        <Typography sx={{ color: textFaint, fontSize: '.8rem' }}>{row.label}</Typography>
                                        <Typography sx={{ color: row.label === 'Total Debit' ? accentColor : textPri, fontWeight: row.label === 'Total Debit' ? 700 : 600, fontSize: '.85rem' }}>{row.value}</Typography>
                                    </Box>
                                    {i < arr.length - 1 && <Divider sx={{ borderColor: dividerC }} />}
                                </React.Fragment>
                            ))}

                            <Box sx={{ mt: 2, p: 1.5, borderRadius: '12px', background: alpha(accentColor, .08), border: `1px solid ${alpha(accentColor, .2)}` }}>
                                <Typography sx={{ color: accentColor, fontSize: '.75rem', fontWeight: 600, textAlign: 'center' }}>
                                    {fmt(numAmount)} will be deducted from your wallet
                                </Typography>
                            </Box>
                        </Box>

                        <Button fullWidth onClick={handleConfirm} disabled={loading}
                                sx={{
                                    py: 1.65, borderRadius: '14px', textTransform: 'none', fontWeight: 700, fontSize: '.95rem',
                                    background: `linear-gradient(135deg, ${B.purple}, ${B.light})`,
                                    color: '#fff', boxShadow: `0 12px 32px ${alpha(B.purple, .35)}`,
                                    '&:disabled': { opacity: .5, boxShadow: 'none' },
                                }}>
                            {loading ? 'Processing…' : `Pay ${fmt(numAmount)}`}
                        </Button>
                    </>
                )}

                {/* ── Recent Transactions ── */}
                <Box sx={{ ...glassCard, mt: 3, overflow: 'hidden' }}>
                    <Box sx={{ px: 2.5, pt: 2.5, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '.95rem', letterSpacing: '-.02em', color: textPri }}>Recent Bills</Typography>
                        <Button size="small" onClick={() => navigate('/history')}
                                sx={{ color: B.purple, fontWeight: 600, fontSize: '.7rem', textTransform: 'none', px: 1, borderRadius: 2, '&:hover': { background: alpha(B.purple, .07) } }}>
                            See all
                        </Button>
                    </Box>

                    {RECENT_TXS.map((tx, i) => (
                        <React.Fragment key={tx.id}>
                            <Box sx={{ px: 2.5, py: 1.75, display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer', transition: 'background .15s', '&:hover': { background: alpha(isDark ? '#fff' : B.dark, .025) } }}>
                                <Box sx={{ width: 40, height: 40, borderRadius: 2.5, flexShrink: 0, background: B.redDim, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <NorthEastOutlined sx={{ color: B.red, fontSize: 16 }} />
                                </Box>
                                <Box flex={1} minWidth={0}>
                                    <Typography sx={{ fontWeight: 600, fontSize: '.84rem', color: textPri, letterSpacing: '-.015em' }}>{tx.title}</Typography>
                                    <Typography sx={{ fontSize: '.68rem', color: textFaint, mt: .15 }}>{tx.sub} · {tx.date}</Typography>
                                </Box>
                                <Typography sx={{ fontWeight: 700, fontSize: '.86rem', color: textPri, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                                    −₦{tx.amount.toLocaleString()}
                                </Typography>
                            </Box>
                            {i < RECENT_TXS.length - 1 && <Divider sx={{ mx: 2.5, borderColor: alpha(isDark ? '#fff' : B.dark, .055) }} />}
                        </React.Fragment>
                    ))}

                    <Box sx={{ px: 2.5, py: 2, borderTop: `1px solid ${alpha(isDark ? '#fff' : B.dark, .055)}` }}>
                        <Button fullWidth onClick={() => navigate('/history')}
                                sx={{ color: textFaint, textTransform: 'none', fontWeight: 600, fontSize: '.76rem', py: .75, borderRadius: 2, '&:hover': { background: alpha(isDark ? '#fff' : B.dark, .035), color: textPri } }}>
                            View full history
                        </Button>
                    </Box>
                </Box>

                {/* footer */}
                <Box sx={{ mt: 3.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .75 }}>
                    <LockOutlined sx={{ fontSize: 11, color: alpha(isDark ? '#fff' : B.ink, .18) }} />
                    <Typography sx={{ fontSize: '.62rem', color: alpha(isDark ? '#fff' : B.ink, .18), letterSpacing: '.05em' }}>
                        256-BIT ENCRYPTION · NG CREDIT FINANCIAL SERVICES
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default BillsPage;